
import React, { Fragment } from 'react';

import { Icon } from '../../icons';

import AbstractToolboxItem, { type Props } from './AbstractToolboxItem';

/**
 * Web implementation of {@code AbstractToolboxItem}.
 */
export default class ToolboxItem extends AbstractToolboxItem<Props> {
    /**
   * Initializes a new {@code ToolboxItem} instance.
   *
   * @inheritdoc
   */
    constructor(props: Props) {
        super(props);

        this._onKeyDown = this._onKeyDown.bind(this);
    }

    _onKeyDown: (Object) => void;

    /**
   * Handles 'Enter' key on the button to trigger onClick for accessibility.
   * We should be handling Space onKeyUp but it conflicts with PTT.
   *
   * @param {Object} event - The key event.
   * @private
   * @returns {void}
   */
    _onKeyDown(event) {
    // If the event coming to the dialog has been subject to preventDefault
    // we don't handle it here.
        if (event.defaultPrevented) {
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.props.onClick();
        }
    }

    /**
   * Handles rendering of the actual item. If the label is being shown, which
   * is controlled with the `showLabel` prop, the item is rendered for its
   * display in an overflow menu, otherwise it will only have an icon, which
   * can be displayed on any toolbar.
   *
   * @protected
   * @returns {ReactElement}
   */
    _renderItem() {
        const {
            disabled,
            elementAfter,
            onClick,
            showLabel,
            toggled
        } = this.props;
        const className = showLabel ? 'overflow-menu-item' : 'toolbox-button';
        const props = {
            'aria-pressed': toggled,
            'aria-disabled': disabled,
            'aria-label': this.accessibilityLabel,
            className: className + (disabled ? ' disabled' : ''),
            onClick: disabled ? undefined : onClick,
            onKeyDown: this._onKeyDown,
            tabIndex: 0,
            role: 'button'
        };

        const elementType = showLabel ? 'li' : 'div';
        const children = (
            <Fragment>
                {this._renderIcon()}
                {showLabel && <span>{this.label}</span>}
                {elementAfter}
            </Fragment>
        );

        return React.createElement(elementType, props, children);
    }

    /**
   * Helper function to render the item's icon.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderIcon() {
        const { customClass, disabled, icon, showLabel, toggled, extraStyles } = this.props;
        const iconComponent = <Icon src = { icon } />;
        const elementType = showLabel ? 'span' : 'div';

        const className = `${
            showLabel ? 'overflow-menu-item-icon' : 'toolbox-icon'
        } ${toggled ? 'toggled' : ''} ${disabled ? 'disabled' : ''} ${
            customClass ?? ''
        }`;

        return React.createElement(elementType, {
            className,
            style: extraStyles
        }, iconComponent);
    }
}
